<template>
    <section :aria-label="transactionMode ? 'Transaction documents' : 'Document library'" :class="{ 'library-embedded': transactionMode || sourceMode }" class="library-page">
        <header v-if="transactionMode || sourceMode" class="library-panel-header">
            <h2 class="text-base font-semibold">{{ transactionMode ? 'Transaction Documents' : 'Library' }}</h2>
            <label class="library-search">
                <span class="sr-only">{{ transactionMode ? 'Search transaction documents' : 'Search this library' }}</span>
                <search-icon size="16" class="library-search-icon" aria-hidden="true" />
                <input v-model="search" type="search" />
            </label>
        </header>
        <div v-if="!transactionMode" class="library-tabs flex flex-wrap border-b border-gray-300 mb-5" role="tablist" aria-label="Library sections">
            <button v-for="section in sections" :key="section.key" type="button" role="tab"
                :aria-selected="tab === section.key ? 'true' : 'false'" :disabled="busy"
                class="px-5 py-4 border-b-2 text-lg font-semibold"
                :class="tab === section.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-900'"
                @click="switchTab(section.key)">{{ section.label }}</button>
        </div>

        <div v-if="transactionMode" class="library-transaction-actions flex items-center justify-end border-b border-gray-300 mb-5">
            <span class="text-xs text-gray-500 mr-auto">{{ library.documents.length }} {{ library.documents.length === 1 ? 'document' : 'documents' }}</span>
            <p role="status" aria-live="polite" class="library-save-status text-sm text-blue-700" :title="busy ? (progress || 'Saving changes…') : null">{{ busy ? (progress || 'Saving changes…') : '' }}</p>
            <div v-if="library.can_edit" class="library-transaction-buttons flex gap-3">
                <button type="button" class="library-button library-new-folder" :disabled="busy" @click="openAction('folder-create')"><folder-plus-icon size="16" aria-hidden="true" />New Folder</button>
                <button type="button" class="library-button library-primary" :disabled="busy" @click="$refs.files.click()"><upload-icon size="16" aria-hidden="true" />Upload</button>
            </div>
        </div>

        <div v-if="error" role="alert" class="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-700">{{ error }}</div>
        <div v-if="notice" role="status" class="mb-4 p-3 rounded border border-green-200 bg-green-50 text-green-800">{{ notice }}</div>

        <input v-if="library.can_edit" ref="files" type="file" multiple class="hidden" @change="uploadFiles" />
        <div v-if="!(transactionMode || sourceMode) || tab === 'forms'" class="library-toolbar">
            <label v-if="!(transactionMode || sourceMode)" class="library-search">
                <span class="sr-only">{{ transactionMode ? 'Search transaction documents' : 'Search this library' }}</span>
                <search-icon size="16" class="library-search-icon" aria-hidden="true" />
                <input v-model="search" type="search" placeholder="Search documents and folders" />
            </label>
            <label v-if="tab === 'forms'" class="text-sm">
                <span class="sr-only">Transaction type</span>
                <select v-model="side" class="border border-gray-300 rounded px-3 py-2" @change="navigate(null)">
                    <option value="all">All MLS Forms</option><option value="buyer">Buyer</option><option value="seller">Seller</option>
                </select>
            </label>
            <p role="status" aria-live="polite" class="library-save-status text-sm text-blue-700" :title="busy ? (progress || 'Saving changes…') : null">{{ busy ? (progress || 'Saving changes…') : '' }}</p>
            <div v-if="library.can_edit && !transactionMode" class="library-toolbar-actions">
                <button type="button" class="library-button library-new-folder" :disabled="busy" @click="openAction('folder-create')">
                    <folder-plus-icon size="16" aria-hidden="true" />New Folder
                </button>
                <button type="button" class="library-button library-primary" :disabled="busy" @click="$refs.files.click()">
                    <upload-icon size="16" aria-hidden="true" />Upload
                </button>
            </div>
        </div>

        <div @dragover="dragOver" @drop="dropOnRoot" class="library-tree border border-gray-300 rounded bg-white" :aria-busy="busy ? 'true' : 'false'">
            <library-folder-list :folders="folderTree" :documents="rootDocuments" :open-ids="openFolders" :selected-folder-id="folderId"
                :busy="busy" :can-edit="library.can_edit" :can-reorder="canReorder" :searching="!!search" :can-clone="!sourceMode && tab !== 'personal'" :can-add="sourceMode && addEnabled" :can-drop="transactionMode && library.can_edit && !busy"
                @add-document="$emit('add-document', { document: $event, type: tab })" @drop-document="dropDocument" @move-document="moveDocument"
                @toggle="toggleFolder" @menu="openMenu($event.event, $event.kind, $event.item)"
                @reorder-folders="reorderFolders($event.folders, $event.parentId)" @reorder-documents="reorderDocuments($event.documents, $event.parentId)" />
            <div v-if="!folderTree.length && !rootDocuments.length" class="library-empty">
                <folder-icon size="26" class="mx-auto text-gray-400 mb-3" aria-hidden="true" />
                <p class="font-medium text-gray-600">{{ search ? 'No matching documents or folders' : 'Your library is empty' }}</p>
                <p class="text-xs text-gray-500 mt-2">{{ search ? 'Try a different search.' : library.can_edit ? 'Upload a document or create a folder to get started.' : 'Documents will appear here when they are added.' }}</p>
            </div>
        </div>
        <div class="library-footer flex flex-wrap justify-between gap-x-2 gap-y-1 text-xs text-gray-500 mt-3">
            <div v-if="sourceMode" class="flex flex-col gap-1">
                <span v-if="addEnabled">Drag a file to the transaction, or click the <arrow-right-icon size="12" class="inline-block align-middle relative -top-0.5" aria-label="right arrow" /> to add it to the selected folder.</span>
                <span>To edit your master library, <a href="/broker/library" class="text-blue-600 underline">click here</a></span>
            </div>
            <span v-if="library.can_edit">Maximum upload size: 60 MB per file. Drag files and folders to reorder.</span>
            <span v-if="library.can_edit">Uploading to: <strong class="font-semibold text-gray-700">{{ currentFolderName }}</strong><button v-if="folderId !== null" type="button" class="text-blue-600 ml-4 underline" :disabled="busy" @click="folderId = null">Use top level</button></span>
        </div>

        <contextual-dropdown :show="menu.show" :items="menuItems" :x="menu.x" :y="menu.y" @item-clicked="handleMenuAction" @close="closeMenu" />

        <modal :name="modalName" :adaptive="true" :focusTrap="true" :width="480" height="auto" :clickToClose="!busy" @before-close="beforeClose" @opened="focusName">
            <form class="p-6 bg-white" @submit.prevent="submitAction">
                <h2 class="library-modal-heading text-lg font-semibold text-gray-900 mb-4">{{ actionTitle }}</h2>
                <div v-if="action.includes('delete')" class="text-sm text-gray-600 mb-4 min-w-0">
                    <p class="truncate mb-2" :title="selected.name || selected.display_filename">{{ selected.name || selected.display_filename }}</p>
                    <p v-if="!removingTransactionDocument" class="mb-0">{{ action === 'folder-delete' ? 'Delete this folder and all documents and subfolders inside it?' : 'Delete this document?' }} This cannot be undone.</p>
                </div>
                <label v-if="action.includes('rename') || action === 'folder-create'" class="block text-sm">
                    Name<input ref="actionName" v-model="name" required maxlength="255" class="block w-full border border-gray-300 rounded px-3 py-2 mt-2 mb-4" />
                </label>
                <label v-if="action.includes('move') || action.includes('clone')" class="block text-sm mb-4">
                    Destination folder
                    <select v-model="destination" class="block w-full border border-gray-300 rounded px-3 py-2 mt-2">
                        <option :value="null">{{ action.includes('clone') ? 'My Documents' : sectionLabel }} (top level)</option>
                        <option v-for="folder in destinationFolders" :key="folder.id" :value="folder.id">{{ folder.label }}</option>
                    </select>
                </label>
                <p v-if="action.includes('clone')" class="text-sm text-gray-500 mb-4">This creates your own copy. Changes to the original will not change your copy.</p>
                <p v-if="actionError" role="alert" class="text-sm text-red-700 mb-4">{{ actionError }}</p>
                <div class="flex justify-end gap-3">
                    <button type="button" class="library-button" :disabled="busy" @click="$modal.hide(modalName)">Cancel</button>
                    <button type="submit" class="library-button" :class="action.includes('delete') ? 'library-danger' : 'library-primary'" :disabled="busy">{{ busy ? 'Saving…' : action.includes('delete') ? (removingTransactionDocument ? 'Remove' : 'Delete permanently') : action.includes('clone') ? 'Copy document' : 'Save' }}</button>
                </div>
            </form>
        </modal>
    </section>
</template>

<script>
import { FolderIcon, FolderPlusIcon, UploadIcon, SearchIcon, ArrowRightIcon } from 'vue-feather-icons';
import ContextualDropdown from '../../components/contextual-dropdown.vue';
import LibraryFolderList from './library-folder-list.vue';

export default {
    components: { FolderIcon, FolderPlusIcon, UploadIcon, SearchIcon, ArrowRightIcon, ContextualDropdown, LibraryFolderList },
    props: {
        personal_library: { required: true }, team_library: { default: null }, forms_library: { default: null },
        transactionMode: { type: Boolean, default: false }, sourceMode: { type: Boolean, default: false },
        storageKey: { type: String, default: null },
        addEnabled: { type: Boolean, default: false },
    },
    data() {
        return {
            libraries: { personal: this.personal_library, team: this.team_library, forms: this.forms_library },
            tab: 'personal', folderId: null, side: 'all', search: '', busy: false, error: '', notice: '', progress: '',
            openFolders: [],
            menu: { show: false, kind: '', item: {}, x: 0, y: 0 },
            menuTrigger: null,
            action: '', selected: {}, name: '', destination: null, actionError: '',
        };
    },
    computed: {
        modalName() { return `library-action-${this._uid}`; },
        sections() {
            return [{ key: 'forms', label: 'MLS Forms' }, { key: 'personal', label: 'My Documents' }, { key: 'team', label: "My Team's Documents" }].filter(section => this.libraries[section.key]);
        },
        sectionLabel() { if (this.transactionMode) return 'Transaction Documents'; return this.sections.find(section => section.key === this.tab)?.label || 'My Documents'; },
        library() { return this.libraries[this.tab] || this.libraries.personal; },
        canReorder() { return this.library.can_edit && !this.search && !this.busy; },
        currentFolderName() { return this.folderChain.map(folder => folder.name).join(' / ') || this.sectionLabel; },
        folderChain() {
            const path = [];
            let folder = this.library.folders.find(item => item.id === this.folderId);
            while (folder && !path.some(item => item.id === folder.id)) {
                path.unshift(folder);
                folder = this.library.folders.find(item => item.id === folder.parent_id);
            }
            return path;
        },
        rootDocuments() { return this.documentsFor(null); },
        folderTree() {
            const query = this.search.toLowerCase();
            const build = (parentId, ancestorMatch = false, ancestors = []) => {
                return this.sort(this.library.folders.filter(folder => folder.parent_id == parentId && this.matchesSide(folder)))
                    .filter(folder => !ancestors.includes(folder.id))
                    .map(folder => {
                        const matched = ancestorMatch || (query && folder.name.toLowerCase().includes(query));
                        const children = build(folder.id, matched, [...ancestors, folder.id]);
                        const documents = this.sort(this.library.documents.filter(document => document.folder_id === folder.id
                            && (!query || matched || document.display_filename.toLowerCase().includes(query))));
                        return { ...folder, children, documents, matched, documentCount: documents.length + children.reduce((total, child) => total + child.documentCount, 0) };
                    }).filter(folder => !query || folder.matched || folder.children.length || folder.documents.length);
            };
            return build(null);
        },
        menuItems() {
            const { kind, item } = this.menu;
            if (!kind) return [];
            const items = [];
            const add = (key, label, icon, extra = {}) => items.push({ key, label, icon, ...extra });
            if (kind === 'document') {
                if (item.download_route) add('download', 'Download', 'far fa-arrow-alt-circle-down', { href: item.download_route });
                if (item.show_route) add('view', 'View', 'far fa-eye', { href: item.show_route });
                if (!this.sourceMode && this.tab !== 'personal' && item.clone_route) add('document-clone', 'Copy to My Documents', 'far fa-copy');
            }
            if (this.library.can_edit) {
                if (kind === 'folder') add('folder-create-child', 'New subfolder', 'far fa-folder');
                add(`${kind}-rename`, 'Rename', 'far fa-edit');
                if (!this.search) {
                    const parentId = kind === 'folder' ? item.parent_id : item.folder_id;
                    const siblings = kind === 'folder' ? this.foldersFor(parentId) : this.documentsFor(parentId);
                    const index = siblings.findIndex(sibling => sibling.id === item.id);
                    if (index > 0) add('earlier', 'Move earlier', 'fas fa-arrow-up');
                    if (index >= 0 && index < siblings.length - 1) add('later', 'Move later', 'fas fa-arrow-down');
                }
                add(`${kind}-move`, 'Move to folder', 'far fa-folder-open');
                add(`${kind}-delete`, this.transactionMode && kind === 'document' ? 'Remove' : 'Delete', 'far fa-trash-alt', { color: 'red' });
            }
            return items;
        },
        removingTransactionDocument() { return this.transactionMode && this.action === 'document-delete'; },
        actionTitle() {
            if (this.removingTransactionDocument) return 'Remove the document from this transaction?';
            return { 'folder-create': 'New folder', 'folder-rename': 'Rename folder', 'folder-move': 'Move folder', 'folder-delete': 'Delete folder and contents', 'document-rename': 'Rename document', 'document-move': 'Move document', 'document-delete': 'Delete document', 'document-clone': 'Copy to My Documents' }[this.action];
        },
        destinationFolders() {
            const library = this.action.includes('clone') ? this.libraries.personal : this.library;
            const excluded = this.action === 'folder-move' ? this.descendantIds(this.selected.id) : [];
            const result = [];
            const visit = (parentId, prefix) => {
                this.sort(library.folders.filter(folder => folder.parent_id == parentId)).forEach(folder => {
                    if (excluded.includes(folder.id) || result.some(item => item.id === folder.id)) return;
                    const label = prefix + folder.name;
                    result.push({ id: folder.id, label });
                    visit(folder.id, label + ' / ');
                });
            };
            visit(null, '');
            return result;
        },
    },
    created() { this.restoreExpandedFolders(); },
    watch: {
        openFolders: { deep: true, handler() { this.saveExpandedFolders(); } },
        storageKey() { this.restoreExpandedFolders(); },
        search() { this.closeMenu(); },
    },
    methods: {
        expandedFoldersKey() {
            return this.storageKey ? `${this.storageKey}:${this.tab}:${this.library.id}:expanded` : null;
        },
        saveExpandedFolders() {
            const key = this.expandedFoldersKey();
            if (!key) return;
            try { localStorage.setItem(key, JSON.stringify(this.openFolders)); }
            catch (_) { /* Storage may be disabled or full; folder navigation still works. */ }
        },
        restoreExpandedFolders() {
            const key = this.expandedFoldersKey();
            if (!key) return;
            let stored = [];
            try {
                const value = JSON.parse(localStorage.getItem(key));
                if (Array.isArray(value)) stored = value;
            } catch (_) { /* Ignore unavailable storage or malformed saved state. */ }
            this.openFolders = this.library.folders.filter(folder => stored.includes(folder.id)).map(folder => folder.id);
        },
        dragOver(event) {
            if (this.transactionMode && this.library.can_edit && !this.busy && event.dataTransfer.types.includes('application/x-c2c-library-document')) {
                event.preventDefault(); event.dataTransfer.dropEffect = 'copy';
            }
        },
        dropOnRoot(event) {
            if (!this.transactionMode || !this.library.can_edit || this.busy) return;
            const raw = event.dataTransfer.getData('application/x-c2c-library-document');
            if (!raw) return;
            event.preventDefault(); event.stopPropagation();
            this.dropDocument({ raw, folderId: null });
        },
        dropDocument(payload) {
            try { this.$emit('drop-document', { ...JSON.parse(payload.raw), folderId: payload.folderId }); } catch (_) { /* Ignore unrelated drops. */ }
        },
        async moveDocument({ document, folderId }) {
            if (!this.canReorder || document.folder_id === folderId || !document.update_route) return;
            if (folderId !== null && !this.library.folders.some(folder => folder.id === folderId)) return;
            this.busy = true; this.error = ''; this.notice = '';
            try {
                await axios.patch(document.update_route, { folder_id: folderId });
                this.folderId = folderId;
                if (folderId !== null && !this.openFolders.includes(folderId)) this.openFolders.push(folderId);
                await this.refresh();
            } catch (error) { this.error = this.message(error); }
            finally { this.busy = false; }
        },
        async addSource(document, folderId = this.folderId) {
            if (this.busy || !this.library.can_edit || !document.add_route) return;
            this.busy = true; this.error = ''; this.notice = '';
            try {
                await axios.post(document.add_route, { folder_id: folderId });
                this.folderId = folderId; this.search = '';
                await this.refresh();
            } catch (error) { this.error = this.message(error); }
            finally { this.busy = false; }
        },
        sort(items) { return [...items].sort((a, b) => (a.order || 0) - (b.order || 0) || (a.name || a.display_filename).localeCompare(b.name || b.display_filename)); },
        matchesSide(folder) { return this.tab !== 'forms' || this.side === 'all' || (!folder.is_buyer_side && !folder.is_seller_side) || folder[`is_${this.side}_side`]; },
        switchTab(tab) {
            this.saveExpandedFolders();
            this.closeMenu(); this.tab = tab; this.folderId = null; this.openFolders = []; this.search = ''; this.error = ''; this.notice = '';
            this.restoreExpandedFolders();
        },
        navigate(id) {
            this.closeMenu(); this.folderId = id; this.search = '';
            this.revealFolder(id);
        },
        revealFolder(id) {
            const seen = [];
            let folder = this.library.folders.find(item => item.id === id);
            while (folder && !seen.includes(folder.id)) {
                seen.push(folder.id);
                if (!this.openFolders.includes(folder.id)) this.openFolders.push(folder.id);
                folder = this.library.folders.find(item => item.id === folder.parent_id);
            }
        },
        toggleFolder(folder) {
            this.closeMenu();
            if (this.search) { this.navigate(folder.id); return; }
            if (this.openFolders.includes(folder.id)) {
                this.openFolders = this.openFolders.filter(id => id !== folder.id);
                if (this.descendantIds(folder.id).includes(this.folderId)) this.folderId = folder.parent_id;
            } else {
                this.openFolders.push(folder.id);
                this.folderId = folder.id;
            }
        },
        foldersFor(parentId) {
            const query = this.search.toLowerCase();
            return this.sort(this.library.folders.filter(folder => this.matchesSide(folder)
                && folder.parent_id == parentId && (!query || folder.name.toLowerCase().includes(query))));
        },
        documentsFor(parentId) {
            const query = this.search.toLowerCase();
            return this.sort(this.library.documents.filter(document => {
                const folder = this.library.folders.find(item => item.id === document.folder_id);
                return (!folder || this.matchesSide(folder))
                    && document.folder_id == parentId && (!query || document.display_filename.toLowerCase().includes(query));
            }));
        },
        openMenu(event, kind, item) {
            if (this.busy) return;
            const rect = event.currentTarget.getBoundingClientRect();
            this.menuTrigger = event.currentTarget;
            this.menu = { show: true, kind, item, x: rect.right, y: rect.bottom + 20 };
        },
        closeMenu() {
            const wasOpen = this.menu.show;
            this.menu.show = false;
            if (wasOpen && this.menuTrigger?.isConnected) this.menuTrigger.focus({ preventScroll: true });
        },
        handleMenuAction(action) {
            const { kind, item } = this.menu;
            this.closeMenu();
            if (action.key === 'view') window.open(action.href, '_blank', 'noopener');
            else if (action.key === 'download') window.location.href = action.href;
            else if (action.key === 'earlier' || action.key === 'later') this.shiftItem(kind, item, action.key === 'earlier' ? -1 : 1);
            else if (action.key === 'folder-create-child') { this.navigate(item.id); this.openAction('folder-create'); }
            else this.openAction(action.key, item);
        },
        parentPath(id) {
            const parts = [];
            const seen = [];
            let folder = this.library.folders.find(item => item.id == id);
            while (folder && !seen.includes(folder.id)) { seen.push(folder.id); parts.unshift(folder.name); folder = this.library.folders.find(item => item.id === folder.parent_id); }
            return [this.sectionLabel, ...parts].join(' / ');
        },
        descendantIds(id) {
            const ids = [id];
            for (let index = 0; index < ids.length; index++) this.library.folders.filter(folder => folder.parent_id === ids[index]).forEach(folder => { if (!ids.includes(folder.id)) ids.push(folder.id); });
            return ids;
        },
        folderCount(id) { const ids = this.descendantIds(id); const count = this.library.documents.filter(document => ids.includes(document.folder_id)).length; return `${count} ${count === 1 ? 'document' : 'documents'}`; },
        message(error) { return Object.values(error.response?.data?.errors || {}).flat()[0] || error.response?.data?.message || 'Something went wrong. Please try again.'; },
        async refresh() {
            const response = await axios.get(this.libraries.personal.routes.index, { headers: { Accept: 'application/json' } });
            this.libraries = this.transactionMode
                ? { personal: response.data.transaction_library, team: null, forms: null }
                : { personal: response.data.personal_library, team: response.data.team_library, forms: response.data.forms_library };
            if (this.transactionMode) this.$emit('updated', response.data);
            if (!this.libraries[this.tab]) this.switchTab('personal');
            if (!this.library.folders.some(folder => folder.id === this.folderId)) this.folderId = null;
            this.openFolders = this.openFolders.filter(id => this.library.folders.some(folder => folder.id === id));
            this.revealFolder(this.folderId);
        },
        openAction(action, selected = {}) {
            this.closeMenu();
            this.action = action; this.selected = selected; this.name = selected.name || selected.display_filename || '';
            this.destination = action.includes('clone') ? null : selected.parent_id ?? selected.folder_id ?? null;
            this.actionError = ''; this.$modal.show(this.modalName);
            this.$nextTick(() => this.$refs.actionName?.focus());
        },
        focusName() { this.$refs.actionName?.focus(); },
        beforeClose(event) { if (this.busy) event.cancel(); },
        async submitAction() {
            if (this.busy) return;
            this.busy = true; this.actionError = ''; this.error = ''; this.notice = '';
            const folderUrl = this.selected.update_route || `/broker/library-folders/${this.selected.id}`;
            try {
                if (this.action === 'folder-create') await axios.post(this.library.routes.store_folder, { name: this.name, parent_id: this.folderId });
                if (this.action === 'folder-rename') await axios.patch(folderUrl, { name: this.name });
                if (this.action === 'folder-move') await axios.patch(folderUrl, { parent_id: this.destination });
                if (this.action === 'folder-delete') await axios.delete(folderUrl, { data: { confirm: true } });
                if (this.action === 'document-rename') await axios.patch(this.selected.update_route, { title: this.name });
                if (this.action === 'document-move') await axios.patch(this.selected.update_route, { folder_id: this.destination });
                if (this.action === 'document-delete') await axios.delete(this.selected.delete_route);
                if (this.action === 'document-clone') await axios.post(this.selected.clone_route, { folder_id: this.destination });
                this.busy = false; this.$modal.hide(this.modalName); this.busy = true;
                if (this.action === 'document-clone') { this.tab = 'personal'; this.openFolders = []; this.folderId = this.destination; this.search = ''; }
                if (this.action.endsWith('-move')) this.folderId = this.destination;
                await this.refresh();
                this.notice = this.action === 'document-clone' ? 'Document copied to My Documents.' : '';
            } catch (error) { this.actionError = this.message(error); this.error = this.actionError; }
            finally { this.busy = false; }
        },
        async uploadFiles(event) {
            const files = Array.from(event.target.files); event.target.value = '';
            if (!files.length || this.busy) return;
            this.busy = true; this.error = ''; this.notice = '';
            try {
                for (const file of files) {
                    if (file.size > 60000000) throw new Error(`${file.name} exceeds the 60 MB limit.`);
                    const data = new FormData(); data.append('file', file);
                    if (this.folderId !== null) data.append('folder_id', this.folderId);
                    this.progress = `Uploading ${file.name}…`;
                    await axios.post(this.library.routes.store_document, data);
                }
            } catch (error) { this.error = error.response ? this.message(error) : error.message; }
            finally {
                try { await this.refresh(); } catch (error) { this.error = this.message(error); }
                this.busy = false; this.progress = '';
            }
        },
        async shiftItem(kind, item, direction) {
            const parentId = kind === 'folder' ? item.parent_id : item.folder_id;
            const items = kind === 'folder' ? this.foldersFor(parentId) : this.documentsFor(parentId);
            const index = items.findIndex(sibling => sibling.id === item.id);
            const target = index + direction;
            if (target < 0 || target >= items.length) return;
            [items[index], items[target]] = [items[target], items[index]];
            if (kind === 'folder') await this.reorderFolders(items, parentId);
            else await this.reorderDocuments(items, parentId);
        },
        async reorderFolders(folders, parentId) { await this.reorder(this.library.routes.reorder_folders, { parent_id: parentId, folder_ids: folders.map(folder => folder.id) }); },
        async reorderDocuments(documents, parentId) { await this.reorder(this.library.routes.reorder_documents, { folder_id: parentId, document_ids: documents.map(document => document.id) }); },
        async reorder(url, data) {
            if (!this.canReorder) return;
            this.busy = true; this.error = ''; this.notice = '';
            try { await axios.post(url, data); await this.refresh(); }
            catch (error) { this.error = this.message(error); }
            finally { this.busy = false; }
        },
    },
};
</script>

<style scoped>
.library-embedded { display: flex; flex: 1; flex-direction: column; min-height: 0; --library-folder-padding: 0.15rem 0.75rem; --library-folder-toggle-padding: 0.25rem 0; --library-folder-font-size: 0.875rem; --library-document-padding: 0.2rem 0.75rem; --library-document-min-height: 2.125rem; }
.library-tabs button[aria-selected="true"] { border-bottom-width: 3px; }
.library-panel-header { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.5rem 0.75rem; border-bottom: 1px solid #e5e7eb; background: #f9fafb; }
.library-panel-header h2 { margin: 0; }
.library-panel-header .library-search { margin-left: auto; }
.library-embedded > * { flex-shrink: 0; }
.library-embedded .library-tabs { flex-wrap: nowrap; overflow-x: auto; margin: 0; min-height: 45px; }
.library-embedded .library-tabs button { padding: 0.65rem 0.75rem; font-size: 0.875rem; white-space: nowrap; }
.library-transaction-actions { min-height: 45px; padding: 0.35rem 0.75rem; margin: 0; }
.library-transaction-buttons { width: 14rem; flex-shrink: 0; }
.library-transaction-buttons .library-button { flex: 1; }
.library-transaction-actions .library-button { padding: 0.35rem 0.65rem; font-size: 0.8125rem; }
.library-embedded .library-toolbar { flex-wrap: nowrap; gap: 0.5rem; margin: 0; padding: 0.5rem 0.75rem; }
.library-embedded .library-search { flex: 0 1 14rem; width: 14rem; min-width: 0; }
.library-embedded .library-search input { border-color: #e5e7eb; background: #fdfdfd; padding: 0.35rem 0.75rem 0.35rem 2rem; font-size: 0.8125rem; }
.library-embedded .library-search input::placeholder { color: #9ca3af; opacity: 1; }
.library-embedded .library-search-icon { left: 0.65rem; color: #9ca3af; }
.library-embedded .library-save-status { order: 0; flex: 1 1 0; min-width: 0; font-size: 0.75rem; }
.library-embedded .library-tree { flex: 1 1 0; min-height: 0; overflow-y: auto; overscroll-behavior: auto; border: 0; border-top: 1px solid #e5e7eb; border-bottom: 1px solid #e5e7eb; border-radius: 0; }
.library-embedded .library-footer { margin: 0; padding: 0.625rem 0.75rem; }
.library-modal-heading { padding-bottom: 1rem; }
.library-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 0.75rem; margin-bottom: 1rem; }
.library-search { position: relative; width: 22rem; max-width: 100%; }
.library-search input { width: 100%; border: 1px solid #d1d5db; border-radius: 9999px; padding: 0.6rem 1rem 0.6rem 2.5rem; font-size: 0.875rem; }
.library-search-icon { position: absolute; left: 0.9rem; top: 50%; transform: translateY(-50%); color: #9ca3af; pointer-events: none; }
.library-save-status { flex: 1 1 0; min-width: 8rem; height: 1.25rem; margin: 0; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.library-toolbar-actions { display: flex; gap: 0.75rem; margin-left: auto; }
.library-button { display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; padding: 0.55rem 0.9rem; font-size: 0.875rem; background: white; color: #374151; white-space: nowrap; }
.library-new-folder:hover:not(:disabled) { border-color: #2563eb; color: #2563eb; }
.library-primary { background: #2563eb; border-color: #2563eb; color: white; }
.library-danger { background: #b91c1c; border-color: #b91c1c; color: white; }
button:disabled { opacity: 0.5; cursor: default; }
.library-empty { padding: 3rem 1.5rem; text-align: center; font-size: 0.875rem; }
button:focus-visible, a:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
.library-tabs button:focus { outline: none; box-shadow: none; }
.library-tabs button:focus-visible { text-decoration: underline; text-underline-offset: 4px; }
@media (max-width: 640px) {
    .library-search { width: 100%; max-width: 22rem; }
    .library-save-status { order: 3; flex-basis: 100%; }
}
</style>
